import { useNavigate, useParams } from "react-router-dom";
import { ApiOperationItems } from "~enums/apiOperationItems";
import { useApiOperations } from "~hooks/useApiOperations.ts";
import type { CreateLeague, UpdateLeague } from "~pages/Leagues/types/upsertLeague.ts";
import { useCreateLeague } from "./useCreateLeague.ts";
import { useDeleteLeague } from "./useDeleteLeague.ts";
import { useUpdateLeague } from "./useUpdateLeague.ts";

export const useLeagueService = () => {
  const { mutateAsync: createLeagueFn, isPending: isCreatePending } = useCreateLeague();
  const { mutateAsync: updateLeagueFn, isPending: isUpdatePending } = useUpdateLeague();
  const { mutateAsync: deleteLeagueFn, isPending: isDeletePending } = useDeleteLeague();
  const { customerId, sport, organizationId } = useParams();
  const navigate = useNavigate();

  const { create, update, remove } = useApiOperations(ApiOperationItems.LEAGUE);

  const createLeague = async (payload: CreateLeague) => {
    const createdLeague = await create(() => createLeagueFn(payload), {
      successMessageId: "league.create.success",
    });

    if (createdLeague) {
      navigate(`/${customerId}/admin/${sport}/${organizationId}/leagues`, { state: { search: payload.nameLocal } });
    }

    return createdLeague;
  };

  const updateLeague = (leagueId: string, payload: Omit<UpdateLeague, "leagueId">) =>
    update(() => updateLeagueFn({ ...payload, leagueId }), { successMessageId: "league.update.success" });

  const deleteLeague = async (leagueId: string) =>
    Boolean(await remove(() => deleteLeagueFn(leagueId), { successMessageId: "league.delete.success" }));

  return {
    createLeague,
    isCreatePending,
    updateLeague,
    isUpdatePending,
    deleteLeague,
    isDeletePending,
  };
};
